import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityTypeEnum, OfferInterface } from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TranslationKeySubPathEnum } from '~/core/enums/translation-key-sub-path.enum';
import { Activity } from '~/core/model/activity.model';
import { FormControlService } from '~/core/services/form/form-control.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { PriceValidator } from '~/core/validators/price/price.validator';
import { ActivitySelectionService } from '~/organizer/activity-planning/services/activity-selection.service';
import { FormPlansEditionModeEnum } from '~/organizer/shared/components/shared/plans/plans.component';
import { buildOfferIdentifier } from '~/organizer/shared/helpers/offer/build-offer-identifier/build-offer-identifier.helper';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-timeslot-pre-schedule-plans',
  templateUrl: './plans.component.html',
})
export class OrganizerTimeslotPagePreSchedulePlansComponent
  implements OnInit, OnDestroy
{
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;

  private activity: Activity;
  private translationKeys: Record<string, string>;

  public formPlansEditionMode = FormPlansEditionModeEnum;
  public isTabletDevice: boolean;
  public plans: FormControl;
  public subscriptions = new Subscription();
  public tipsVariant = TipsVariantEnum.WARNING;
  public translationKeySubPath = TranslationKeySubPathEnum;

  constructor(
    private readonly activitySelectionService: ActivitySelectionService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly redirectionService: RedirectionService,
    private readonly matchMediaService: MatchMediaService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly toggleService: ToggleService,
    private readonly formControlService: FormControlService,
    private readonly priceValidator: PriceValidator,
    private readonly translateService: TranslateService,
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.plans = new FormControl(
      [],
      [
        Validators.required,
        this.priceValidator.validatePaymentProvider(
          this.organizerSharedOrganizationService.organization
        ),
      ]
    );
    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;
        const plans =
          this.activitySelectionService.getOverloads(this.activity.sku)
            .offers ?? this.activity.indicativeOffers;
        if (plans?.length === 0 && this.isFreeActivityOnly) {
          plans.push(this.buildDefaultFreeActivity());
        }
        this.plans.setValue(plans);
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.translationKeys = {
      stripe_url: this.localizeRouterService
        .translateRoute('/organizer.onboarding.payment-1')
        .toString(),
    };
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(`/organizer.timeslot.pre-schedule/${this.activity.sku}`)
        .toString(),
    };
  }

  public get formErrors(): string[] {
    return this.formControlService.getErrorMessages(
      this.plans,
      undefined,
      this.translationKeys
    );
  }

  public get hasErrors(): boolean {
    return !!this.plans.errors;
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get isBirthday(): boolean {
    return (
      this.activity?.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      this.activity?.type === ('PRODUCT_TEST' as ActivityTypeEnum)
    );
  }

  public get isFreeActivityOnly(): boolean {
    return this.toggleService.isEnabled(ToggleNameEnum.ActivityFreeOnly);
  }

  public buildDefaultFreeActivity(): OfferInterface {
    return {
      identifier: null,
      name: this.translateService.instant('component.form.plans.default.name'),
      price: 0,
      description: null,
      currency: null,
    };
  }

  public submit(): void {
    const plans: OfferInterface[] = this.plans.value;
    const defaultCurrency = `${this.toggleService.getVariant(
      ToggleNameEnum.Currency,
      'EUR'
    )}`;
    this.activitySelectionService.setOverloads(this.activity.sku, {
      offers: plans.map((plan) => ({
        ...plan,
        price: +plan.price,
        currency: this.activity.startingPrice?.currency || defaultCurrency,
        identifier: buildOfferIdentifier(plan.name),
      })),
    });

    const url = `organizer.timeslot.pre-schedule/${this.activity.sku}`;
    this.redirectionService.redirect(url);
  }
}
